<template lang="pug">
.donation-form
	form(@submit.prevent="send")
		slot(v-if="formState==FORM_STATES.FORM", name="heading-block")
		fieldset
			legend.sf-heading-2 Donation data
			.form-row
				.form-col
					label#donation-project-guid-label(for="donation-project-guid") Supported project
				.form-col
					VueInput(v-model="model.projectName", id="donation-project-guid", :max-width="352", :max-length="50", readonly="true", tabindex="-1")
			.form-row
				.form-col
					label#donation-merchant-name-label(for="donation-merchant-name") Supported organization
				.form-col
					VueInput(v-model="model.merchantName", id="donation-merchant-name", :max-width="352", :max-length="50", readonly="true", tabindex="-1")
			.form-row
				.form-col
					label(for="donation-donated-amount") Choose your own amount
				.form-col
					VueRadioGroup(id="donation-donated-amount" v-model="amount", legend="", :isLegendHidden="true", :verticalAlign="true", :options="AMOUNT_VALUES")
			.dropdown-field.dropdown-field--arrow(v-if="amount=='OTHER'")
				svg.dropdown-arrow(width="32", height="16", viewbox="0 0 32 16", fill="none", xmlns="http://www.w3.org/2000/svg", aria-hidden="true")
					path(fill-rule="evenodd", clip-rule="evenodd", d="M13.6286 1.96444C13.8848 0.850958 14.8574 0 16 0C17.1426 0 18.1152 0.85096 18.3714 1.96444C19.3548 6.23822 22.6268 16 32 16H0C9.37315 16 12.6452 6.23822 13.6286 1.96444Z", fill="#EFF2F9")
				.dropdown-field-container
					.form-row
						.form-col
							label(for="donation-donated-other-amount") Amount
						.form-col
							VueCurrencyInput(
								id="donation-donated-other-amount",
								v-model="amountOther"
								:max-width="174",
								:max-length="50"
								postfix="EUR",
								:decimals="2",
								:isGrouped="true",
								:has-error="hasError('amountOther')",
								@blur="$v.amountOther.$touch()",
								aria-describedby="donation-donated-other-amount-error",
								:aria-invalid="hasError('amountOther')"
							)
							VueErrorMessage(id="donation-donated-other-amount-error", v-if="hasError('amountOther', 'required')", position="bottom") Please enter the amount!

		fieldset
			legend.sf-heading-2 Please fill your personal details &nbsp;
				VueTooltip(placement="bottom", :distance="8")
					span(slot="content")
						p We need your personal data for SimplePay payments, we do not use it for marketing purposes.
			.form-row
				.form-col
					label#donation-donator-name-label(for="donation-donor") Your name
				.form-col
					VueInput(v-model="model.donorName", id="donation-donor", :max-width="352", :max-length="50", :has-error="hasError('model.donorName')", @blur="$v.model.donorName.$touch()", aria-describedby="donation-donor-error", :aria-invalid="hasError('model.donorName')")
					VueErrorMessage(id="donation-donor-error", v-if="hasError('model.donorName', 'required')", position="bottom") Please enter your full name!
			.form-row
				.form-col
					label#donation-donator-email-label(for="donation-donator-email") Your e-mail address
				.form-col
					VueInput(v-model="model.email", id="donation-donator-email", :max-width="352", :max-length="50", :has-error="hasError('model.email')", @blur="$v.model.email.$touch()", aria-describedby="donation-donator-email-error", :aria-invalid="hasError('model.email')")
					VueErrorMessage(id="donation-donator-email-error", v-if="hasError('model.email', 'required')", position="bottom") Please enter your e-mail address!
					VueErrorMessage(id="donation-donator-email-error", v-if="hasError('model.email', 'email')", position="bottom") Please check your e-mail address!
			.form-row
				.form-col
					label#donation-donator-mobile-phone-label(for="donation-donator-mobile-phone-input") Your mobile phone
				.form-col
					VueMobilephoneInput(id="donation-donator-mobile-phone-input", v-model="model.phoneNumber", :max-width="244", prefix="+", addon="e.g. +12345678911232", :max-length="20", mask="####################", :has-error="hasError('model.phoneNumber')", @blur="$v.model.phoneNumber.$touch()", aria-describedby="donation-donator-mobile-phone-error", :aria-invalid="hasError('model.phoneNumber')")
					VueErrorMessage(id="donation-donator-mobile-phone-error", v-if="hasError('model.phoneNumber', 'required')", position="bottom") The telephone number field is required!
			.form-row(v-if="captchaImg")
				.form-col
					label#donation-captcha-label(for="donation-captcha-input") Please enter the code shown in the image!
				.form-col
					VueCaptcha(id="donation-captcha-input", :max-length="5", :img="'data:image/png;base64,' + captchaImg", v-model="model.captchaText", :max-width="104", :has-error="hasError('model.captchaText') || captchaError", @keyup="$emit('captcha-keyup')", @blur="$v.model.captchaText.$touch()", aria-describedby="donation-captcha-error", :aria-invalid="hasError('model.captchaText')")
					VueErrorMessage(id="donation-captcha-error" v-if="hasError('model.captchaText', 'required')", position="bottom") Please enter the code shown in the image!
					VueErrorMessage(id="donation-captcha-error" v-if="captchaError && !hasError('model.captchaText', 'required')" position="bottom") Please verify the entered code!
			hr(aria-hidden="true")
			.form-row
				VueCheckbox(id="donation-accept-privacy-statement-checkbox", v-model="acceptPrivacyStatement", :has-error="hasError('acceptPrivacyStatement')", @blur="$v.acceptPrivacyStatement.$touch()", aria-labelledby="donation-accept-privacy-statement-checkbox", aria-describedby="donation-accept-privacy-statement-error", :aria-invalid="hasError('acceptPrivacyStatement')")
					slot(name="legal-block")
				VueErrorMessage.privacy-statement-error(id="donation-accept-privacy-statement-error", v-if="hasError('acceptPrivacyStatement', 'checked')", position="bottom") Please read and accept the data handling statement!
			.form-row.form-row--submit-button
				vue-button(type="submit") Continue to pay
			a.form-row--simplepay(href="http://simplepay.hu/vasarlo-aff", target="_blank")
				img.logo-cards(src="../../../assets/img/application/donation/simplepay-logo-cards.png", alt="simplepay logo and cards")
</template>
<script>
import VueButton from '@ui-common/vue-button';
import VueInput from '@ui-common/vue-input';
import VueRadioGroup from '@ui-common/vue-radio-group';
import VueCurrencyInput from '@ui-common/vue-currency-input';
import { required } from 'vuelidate/lib/validators';
import email from '../../../common/js/validators/email';
import VueCaptcha from '@ui-common/vue-captcha';
import VueErrorMessage from '@ui-common/vue-error-message';
import VueMobilephoneInput from '@ui-common/vue-mobilephone-input';
import VueCheckbox from '@ui-common/vue-checkbox';
import VueTooltip from '@ui-common/vue-tooltip';
import { FORM_STATES } from '../../../common/js/constants/constants';
import { jumpToFirstError } from '../../../common/js/find-first-error-component/find-first-error-component';
import { errorHandlerUtils } from '../../../common/js/utils/error-handler-utils';
import { isMobile } from '@ui-common/sf-js-utils';
const OTHER = 'OTHER';
const AMOUNT_VALUES = [
	{ key: 5, value: '5 EUR' },
	{ key: 10, value: '10 EUR' },
	{ key: 50, value: '50 EUR' },
	{ key: OTHER, value: 'Different amount' }
];
export default {
	mixins: [jumpToFirstError, errorHandlerUtils, isMobile],
	data: () => ({
		acceptPrivacyStatement: false,
		model: {
			mid: null,
			projectName: null,
			merchantName: null,
			amount: null,
			donorName: null,
			email: null,
			phoneNumber: null,
			captchaText: null
		},
		amount: 5,
		amountOther: null
	}),
	provide() {
		return {
			$v: this.$v
		};
	},
	props: {
		captchaImg: {
			required: true
		},
		captchaError: {
			required: true
		},
		projectName: {
			required: true,
			type: String
		},
		merchantName: {
			required: true,
			type: String
		},
		mid: {
			required: true,
			type: String
		},
		formData: {
			required: true
		},
		formState: {
			required: true
		}
	},
	components: {
		VueInput,
		VueButton,
		VueRadioGroup,
		VueCurrencyInput,
		VueCaptcha,
		VueErrorMessage,
		VueMobilephoneInput,
		VueCheckbox,
		VueTooltip
	},
	validations() {
		return {
			amountOther: this.amount == OTHER ? { required } : {},
			acceptPrivacyStatement: {
				checked: (value) => value === true
			},
			model: {
				donorName: { required },
				email: {
					required,
					email
				},
				phoneNumber: {
					required
				},
				captchaText: this.captchaImg
					? {
							required
					  }
					: {}
			}
		};
	},
	watch: {
		amount: {
			handler(val) {
				if (val != OTHER) {
					this.model.amount = val;
				} else {
					this.model.amount = null;
				}
			},
			immediate: true
		},
		formData: {
			handler(val) {
				if (val) {
					this.model = Object.assign({}, this.model, val);
					if (val.amount) {
						if (AMOUNT_VALUES.map((amount) => amount.key).includes(val.amount)) {
							this.amount = val.amount;
						} else {
							this.amountOther = val.amount;
							this.amount = OTHER;
						}
					}
				}
			},
			immediate: true
		},
		amountOther(val) {
			this.model.amount = val;
		},
		captchaError(val) {
			if (val) {
				this.jumpToFirstError();
			}
		}
	},
	computed: {
		AMOUNT_VALUES: () => AMOUNT_VALUES,
		FORM_STATES: () => FORM_STATES
	},
	methods: {
		send() {
			this.$v.$touch();
			if (this.$v.$invalid) {
				this.jumpToFirstError();
			} else {
				const modelToSend = Object.assign({}, this.model, { mid: this.mid });
				this.$emit('submit', modelToSend);
			}
		},
		resetCaptcha() {
			this.model.captchaText = null;
			this.$v.model.captchaText.$reset();
		}
	},
	created() {
		this.model.projectName = this.projectName;
		this.model.merchantName = this.merchantName;
	}
};
</script>
<style src="@ui-common/vue-input/dist/vue-input.css"></style>
<style src="@ui-common/vue-currency-input/dist/vue-currency-input.css"></style>
<style src="@ui-common/vue-mobilephone-input/dist/vue-mobilephone-input.css"></style>
<style src="@ui-common/vue-error-message/dist/vue-error-message.css"></style>
<style src="@ui-common/vue-captcha/dist/vue-captcha.css"></style>
<style lang="scss">
@import '@ui-common/sf-style-utils/lib/helpers/_queries.scss';

.donation-form .sf-heading-2 {
	padding-left: 0;
	padding-right: 0;
}

.donation-form .form-row {
	padding-left: 0;
	padding-right: 0;
}

.donation-form .privacy-statement-error .sf-error__msg--bottom {
	margin-top: 0px;
}

.donation-form hr {
	display: none;

	@include media('>=tablet') {
		border-left: 0;
		border-right: 0;
		border-top: 0;
		display: block;
		height: 1px;
		margin-bottom: 32px;
		margin-left: 60px;
		margin-right: 60px;
	}

	@include media('>=desktop') {
		margin-left: 84px;
		margin-right: 84px;
	}

	@include media('>=desktop-lg') {
		margin-left: 96px;
		margin-right: 96px;
	}
}

.donation-form .dropdown-field {
	margin-bottom: 48px;
}

.donation-form .dropdown-field--arrow {
	margin-top: -32px;

	@include media('>=tablet') {
		margin-top: -40px;
	}
}

.donation-form .dropdown-field--arrow svg.dropdown-arrow {
	left: 20px;

	@include media('>=tablet', '<desktop') {
		left: 236px;
	}

	@include media('>=desktop') {
		left: 368px;
	}

	@include media('>=desktop-lg') {
		left: 380px;
	}
}

.donation-form > fieldset:first-of-type {
	@include media('>=tablet') {
		margin-bottom: 40px;
	}
}

.donation-form .sf-checkbox:last-of-type {
	margin-bottom: 0;

	@include media('>=tablet') {
		margin-bottom: 16px;
	}
}

.donation-form .form-row--submit-button {
	margin-bottom: 48px;

	@include media('>=tablet') {
		margin-bottom: 64px;
	}
}
.donation-form .form-row--simplepay {
	display: block;
	text-align: center;
}

.donation-form .form-row--simplepay .logo-cards {
	max-width: 736px;
	width: 100%;
}
</style>
